import React, { useEffect, useMemo, useRef } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import PropTypes from "prop-types";

import { FieldAPI, ReportAPI } from "@ai360/core";

import { Checkbox, DateInput, Section, SelectInput, SubSection } from "~/core";
import { ReportOptionsComponent, ReportOptionsComponentProps } from ".";

import { selectors as picklistSelectors, picklistNames, ACTIVE_YN } from "~/core/picklist";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { useAppSelector } from "~/store/hooks";
import { messages } from "~/reports/i18n-messages";
import { model } from "~/reports/data";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { getReportDataByReportType, getCropOptionsData } from "~/reports/data/selectors";

import { getBasemapOptions, getFieldsPerPageOptions } from "../utils";
import { keywords } from "../keywords";
import { getSelectedLayers } from "~/action-panel/components/layer-module/components/layer-list/selectors";
import { ANALYSIS_INFO_NAME_NORMALIZED_YIELD_BENCHMARK } from "~/recs-events/analysis/model";

type IProps = {
    selectedCrop: FieldAPI.ICrop | null;
};

function YieldMapMultiField({
    hideDefaultOptions,
    intl: { formatMessage },
    onInputChange,
    selectedCrop,
}: ReportOptionsComponentProps & IProps) {
    const layerTypeOptions = [
        {
            label: formatMessage(messages.harvestImported),
            value: "harvest",
        },
        {
            label: formatMessage(messages.normalizedYieldBenchmark),
            value: "benchmark",
        },
    ];
    const perPageOptions = getFieldsPerPageOptions();

    const reportData = useAppSelector(getReportDataByReportType(YieldMapMultiField.reportName));
    const basemapOptions = useAppSelector(getBasemapOptions);
    const crops = useAppSelector(getCropOptionsData);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const selectedLayers = useAppSelector(getSelectedLayers);

    const selectedImportedHarvests = useMemo(
        () =>
            Array.from(selectedEventGuidList.values())
                .map((x) => agEventSummaryMap.get(x))
                .filter((e) => e.isImportedYn && e.agEventTypeName == keywords.harvestEvent),
        [selectedEventGuidList, agEventSummaryMap]
    );
    const seasonsToDisplay = useRef<string[]>();

    const optionsDisabled =
        (reportData[model.PROPS_LAYER_TYPE] === "harvest" && selectedImportedHarvests.length > 0) ||
        (reportData[model.PROPS_LAYER_TYPE] === "benchmark" &&
            selectedLayers.some(
                (layer) => layer.layerType === ANALYSIS_INFO_NAME_NORMALIZED_YIELD_BENCHMARK
            ));
    const croppingSeasonDisabled =
        optionsDisabled ||
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        reportData[model.PROPS_LAYER_TYPE] === "benchmark";
    const cropDisabled = optionsDisabled || reportData[model.PROPS_LAYER_TYPE] === "benchmark";

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);
        onInputChange(
            model.PROPS_SELECTED_LAYER_GUIDS,
            selectedLayers.map((x) => x.subLayers[0].surfaceGuid)
        );
    }, [selectedLayers, selectedEventGuidList]);

    useEffect(() => {
        if (hideDefaultOptions) {
            return;
        }
        if (optionsDisabled) {
            const seasonGuids = [];
            if (reportData[model.PROPS_LAYER_TYPE] === "harvest") {
                for (const event of selectedImportedHarvests) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
                const seasonsText = [];
                for (const seasonGuid of seasonGuids) {
                    const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                    if (seasonsText.indexOf(selectedSeason.label) === -1) {
                        seasonsText.push(selectedSeason.label);
                    }
                }
                seasonsToDisplay.current = seasonsText;
            } else if (reportData[model.PROPS_LAYER_TYPE] === "benchmark") {
                seasonsToDisplay.current = [""];
            }
        } else {
            seasonsToDisplay.current = [];
        }
    }, [
        hideDefaultOptions,
        selectedImportedHarvests,
        croppingSeasonDisabled,
        reportData[model.PROPS_LAYER_TYPE],
    ]);

    return (
        <div className="report-options">
            <Section testId="yield-map-multi-field-options-section">
                {hideDefaultOptions ? null : (
                    <React.Fragment>
                        <SubSection testId="yield-map-multi-field-season-and-crop-subsection">
                            <CroppingSeason
                                disabled={croppingSeasonDisabled}
                                required={!croppingSeasonDisabled}
                                reportType={YieldMapMultiField.reportName}
                                valuesToDisplay={seasonsToDisplay.current}
                            />
                            <SelectInput<FieldAPI.ICrop>
                                disabled={cropDisabled}
                                placeholderText={formatMessage(messages.crop)}
                                options={cropDisabled ? [] : crops}
                                onChange={(value) => {
                                    onInputChange(
                                        model.PROPS_CROP_GUID,
                                        value ? value.cropGuid : null
                                    );
                                }}
                                value={cropDisabled ? null : (selectedCrop as FieldAPI.ICrop)}
                                testId="yield-map-multi-field-crop-select"
                            />
                        </SubSection>
                        <SubSection testId="yield-map-multi-field-dates-subsection">
                            <DateInput
                                disabled={optionsDisabled}
                                placeholderText={formatMessage(messages.startDate)}
                                onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_START_DATE]}
                                testId="yield-map-multi-field-start-date-date"
                            />
                            <DateInput
                                disabled={optionsDisabled}
                                placeholderText={formatMessage(messages.endDate)}
                                onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_END_DATE]}
                                testId="yield-map-multi-field-end-date-date"
                            />
                        </SubSection>
                    </React.Fragment>
                )}
                <SubSection testId="yield-map-multi-field-layer-type-subsection">
                    <SelectInput<string>
                        required
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["ymmf-layer-type"]}
                        clearable={false}
                        placeholderText={formatMessage(messages.layerTypeOptions)}
                        options={layerTypeOptions}
                        onChange={(value) => onInputChange(model.PROPS_LAYER_TYPE, value)}
                        value={reportData[model.PROPS_LAYER_TYPE]}
                        testId="yield-map-multi-field-layer-type-select"
                    />
                    <SelectInput<string>
                        required
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["ymmf-per-page"]}
                        clearable={false}
                        placeholderText={formatMessage(messages.fieldsPerPageOptions)}
                        options={perPageOptions}
                        onChange={(value) => onInputChange(model.PROPS_PER_REPORT_OPTION, value)}
                        value={reportData[model.PROPS_PER_REPORT_OPTION]}
                        testId="yield-map-multi-field-per-report-option-select"
                    />
                </SubSection>
                <SubSection testId="yield-map-multi-field-basemap-subsection">
                    <SelectInput
                        required
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["ymmf-basemap-select"]}
                        clearable={false}
                        placeholderText={formatMessage(messages.selectedBasemap)}
                        options={basemapOptions}
                        onChange={(value) => onInputChange(model.PROPS_SELECTED_BASEMAP, value)}
                        value={reportData[model.PROPS_SELECTED_BASEMAP]}
                        testId="yield-map-multi-field-basemap-select"
                    />
                    <Checkbox
                        containerClassNames={["ymmf-custom-imagery"]}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_USE_CUSTOM_IMAGERY, value)
                        }
                        value={reportData[model.PROPS_USE_CUSTOM_IMAGERY]}
                        label={formatMessage(messages.useCustomImagery)}
                        testId="yield-map-multi-field-use-custom-imagery-checkbox"
                    />
                </SubSection>
            </Section>
        </div>
    );
}

YieldMapMultiField.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
    selectedCrop: null,
};

YieldMapMultiField.defaultReportOptions = {
    [model.PROPS_USE_CUSTOM_IMAGERY]: false,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID]: [],
    [model.PROPS_CROP_GUID]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_END_DATE]: null,
    [model.PROPS_LAYER_TYPE]: "harvest",
    [model.PROPS_SELECTED_BASEMAP]: "reportImagery",
    [model.PROPS_PER_REPORT_OPTION]: "grower",
};

YieldMapMultiField.reportPreferences = [
    model.PROPS_SELECTED_BASEMAP,
    model.PROPS_LAYER_TYPE,
    model.PROPS_PER_REPORT_OPTION,
];

YieldMapMultiField.reportName = ReportAPI.ReportNames.YIELD_MAP_MULTI_FIELD;

export default injectIntl(YieldMapMultiField) as ReportOptionsComponent<
    ReportOptionsComponentProps & IProps
>;
